/**
 * @file components/settings/CropFormDrawer.tsx
 * @description 可驗證作物字典新增 / 編輯 Drawer。
 *
 * 受控元件；由父層（CropsPage）管理 open 與當前編輯記錄。
 * 主要類別（mainCategory）在新增時必填且可選；編輯時設為唯讀，
 * 因為主要類別是法規固定分類，變更會影響資料一致性。
 */
import { useEffect } from 'react';
import { Button, Drawer, Form, Input, InputNumber, Select, Space, Switch } from 'antd';
import { message } from '@/lib/antd-app';
import type { CreateCropInput, CropDto, CropMainCategory, CropScheme } from '@zhcb/shared';
import { mainCategoriesForScheme } from '@zhcb/shared';
import { useCreateCrop, useUpdateCrop } from '@/hooks/useCrops';

/** 表單欄位型別定義（對應 Form.Item name） */
interface FormValues {
  scheme: CropScheme;
  mainCategory: CropMainCategory;
  subCategory: string;
  itemGroup?: string | null;
  name: string;
  productScope?: string | null;
  remark?: string | null;
  isActive: boolean;
  sortOrder: number;
}

interface Props {
  open: boolean;
  /** 編輯模式時傳入已選記錄；新增模式時為 null */
  editing: CropDto | null;
  /** 新增模式的預設方案（沿用父層目前選中的 TOP/TAP Tab） */
  defaultScheme: CropScheme;
  onClose: () => void;
}

export function CropFormDrawer({ open, editing, defaultScheme, onClose }: Props) {
  const [form] = Form.useForm<FormValues>();
  const create = useCreateCrop();
  const update = useUpdateCrop();
  /** 依目前表單選中的 scheme 決定主要類別下拉選項（序157：TOP/TAP 分類不同） */
  const scheme = Form.useWatch('scheme', form) ?? defaultScheme;

  /**
   * open 切換 / editing 變更時重置並填充表單。
   * 新增模式預設啟用（isActive: true）、排序為 0、方案沿用父層目前選中的 Tab。
   */
  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        scheme: editing.scheme,
        mainCategory: editing.mainCategory as CropMainCategory,
        subCategory: editing.subCategory,
        itemGroup: editing.itemGroup ?? undefined,
        name: editing.name,
        productScope: editing.productScope ?? undefined,
        remark: editing.remark ?? undefined,
        isActive: editing.isActive,
        sortOrder: editing.sortOrder,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ scheme: defaultScheme, isActive: true, sortOrder: 0 });
    }
  }, [open, editing, defaultScheme, form]);

  /** 方案切換時（僅新增模式可切換）清空主要類別，避免殘留不屬於新方案的舊值 */
  const onSchemeChange = () => {
    form.setFieldValue('mainCategory', undefined);
  };

  /**
   * 提交處理：組裝 payload 後呼叫 create 或 update mutation。
   * 成功後呼叫父層 onClose 關閉 Drawer；失敗顯示錯誤訊息。
   */
  const submit = async (values: FormValues) => {
    const payload: CreateCropInput = {
      scheme: values.scheme,
      mainCategory: values.mainCategory,
      subCategory: values.subCategory,
      itemGroup: values.itemGroup || null,
      name: values.name,
      productScope: values.productScope || null,
      remark: values.remark || null,
      isActive: values.isActive,
      sortOrder: values.sortOrder,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, input: payload });
        message.success('已更新');
      } else {
        await create.mutateAsync(payload);
        message.success('已新增');
      }
      onClose();
    } catch (e) {
      message.error((e as Error).message || '保存失敗');
    }
  };

  return (
    <Drawer
      title={editing ? `編輯作物 #${editing.id}` : '新增作物'}
      open={open}
      onClose={onClose}
      size={520}
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            loading={create.isPending || update.isPending}
            onClick={() => form.submit()}
          >
            {editing ? '儲存' : '提交'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        {/* 驗證方案：新增時可選；編輯時唯讀（切換方案會影響主要類別枚舉，避免破壞資料一致性） */}
        <Form.Item
          name="scheme"
          label="驗證方案"
          rules={[{ required: true, message: '請選擇驗證方案' }]}
        >
          <Select
            disabled={!!editing}
            onChange={onSchemeChange}
            options={[
              { label: '有機 TOP', value: 'TOP' },
              { label: '產銷履歷 TAP', value: 'TAP' },
            ]}
          />
        </Form.Item>

        {/* 主要類別：新增時可選；編輯時唯讀，避免破壞資料分類；選項依上方方案切換 */}
        <Form.Item
          name="mainCategory"
          label="產品類別"
          rules={[{ required: true, message: '請選擇產品類別' }]}
        >
          <Select
            placeholder="請選擇產品類別"
            disabled={!!editing}
            options={mainCategoriesForScheme(scheme).map((c) => ({ label: c, value: c }))}
          />
        </Form.Item>

        {/* 次要類別：如「米類」、「蔬菜類」，必填 */}
        <Form.Item
          name="subCategory"
          label="次要類別"
          rules={[{ required: true, message: '請填寫次要類別', whitespace: true }, { max: 100 }]}
        >
          <Input maxLength={100} placeholder="如：米類、蔬菜類" />
        </Form.Item>

        {/* 品項組：僅蔬菜類需填，其餘可留空 */}
        <Form.Item name="itemGroup" label="品項組" tooltip="僅蔬菜類需填寫，其他類別請留空">
          <Input maxLength={100} placeholder="如：葉菜類（蔬菜類專用）" />
        </Form.Item>

        {/* 品項名稱：作物的實際名稱，必填 */}
        <Form.Item
          name="name"
          label="產品品項"
          rules={[{ required: true, message: '請填寫產品品項', whitespace: true }, { max: 200 }]}
        >
          <Input maxLength={200} placeholder="如：稻米、高麗菜" />
        </Form.Item>

        {/* 產品範圍：僅 TOP 有此層級（序157：有機TOP 比 TAP 多一層產品類別→產品品項→產品範圍） */}
        {scheme === 'TOP' && (
          <Form.Item name="productScope" label="產品範圍" tooltip="僅有機 TOP 使用，TAP 請留空">
            <Input maxLength={200} placeholder="如：甘藍、包心白菜" />
          </Form.Item>
        )}

        {/* 備註：可選說明欄位 */}
        <Form.Item name="remark" label="備註">
          <Input.TextArea rows={3} maxLength={500} showCount placeholder="可填寫補充說明" />
        </Form.Item>

        <Space>
          {/* 啟用狀態 */}
          <Form.Item name="isActive" label="啟用" valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* 排序權重：值越小越靠前 */}
          <Form.Item name="sortOrder" label="排序權重" tooltip="值越小越前">
            <InputNumber min={0} />
          </Form.Item>
        </Space>
      </Form>
    </Drawer>
  );
}
